import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import { SITE_URL } from '../config/seo';
import { StarFilled, StarOutline } from '../components/StarIcon';
import * as LucideIcons from 'lucide-react';
import {
  Heart, ChevronLeft, ChevronRight, FileText, Image as ImageIcon,
  MessageSquare, Phone, MapPin, Clock, Globe, Share2, Flag,
  CheckCircle, Navigation, Calendar, MessageCircle
} from 'lucide-react';
import api from '../utils/api';
import { getCityFromHost } from '../utils/getCityFromHost';
import { isUserLoggedIn, getUserInfo } from '../utils/auth';
import { showSuccess, showError, showConfirm } from '../utils/toast';
import TabbedContent from '../components/TabbedContent';
import ImageGallery from '../components/ImageGallery';
import ReviewBreakdown from '../components/ReviewBreakdown';
import { getTheme } from '../utils/themes';
import { isValidPhone, getTelLink, getWhatsAppLink, formatPhoneDisplay } from '../utils/phoneUtils';
import { trackPhoneView } from '../utils/phoneLogTracker';
import { getImageUrl } from '../utils/imageUtils';

const REPORT_REASONS = ['Incorrect Information', 'Fake/Scam', 'Spam', 'Offensive Content', 'Duplicate Listing', 'Other'];

const BusinessDetail = () => {
  const { categorySlug, businessSlug } = useParams();
  const [business, setBusiness] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 0, comment: '' });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [editingReview, setEditingReview] = useState(null);
  const [userReview, setUserReview] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState({ reason: '', comment: '' });
  const [reportLoading, setReportLoading] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);
  const [favouriteLoading, setFavouriteLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const city = getCityFromHost();
  const navigate = useNavigate();
  const userLoggedIn = isUserLoggedIn();
  const userInfo = getUserInfo();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const reservedWords = ['category', 'admin', 'api', 'login', 'signup', 'profile', 'submit-business', 'about', 'contact', 'terms', 'privacy', 'faq', 'how-it-works'];
        if (reservedWords.includes(categorySlug) || !categorySlug || !businessSlug) {
          if (categorySlug === 'category') {
            navigate(`/${businessSlug}`);
          } else {
            navigate('/');
          }
          return;
        }

        const businessRes = await api.get(`/businesses/${categorySlug}/${businessSlug}`);
        setBusiness(businessRes.data);
        // UNBLOCK UI IMMEDIATELY: Display page without waiting for reviews
        setLoading(false);

        // Fetch reviews in background asynchronously
        if (businessRes.data?.businessId) {
          api.get(`/reviews/business/${businessRes.data.businessId}`)
            .then(reviewsRes => {
              const revs = reviewsRes.data || [];
              setReviews(revs);
              if (userLoggedIn && userInfo) {
                const existingReview = revs.find(
                  review => {
                    const reviewUserId = review.user?._id || review.user;
                    return reviewUserId === userInfo.id || reviewUserId?.toString() === userInfo.id?.toString();
                  }
                );
                setUserReview(existingReview || null);
              } else {
                setUserReview(null);
              }
            })
            .catch(() => setReviews([]));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 404) {
          navigate(`/${categorySlug}`);
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, businessSlug, userLoggedIn, userInfo, navigate]);

  useEffect(() => {
    if (!business?._id) {
      setIsFavourite(false);
      return;
    }
    if (!userLoggedIn) {
      setIsFavourite(false);
      return;
    }
    const check = async () => {
      try {
        const res = await api.get(`/favourites/check/${business._id}`);
        setIsFavourite(res.data?.isFavourite ?? false);
      } catch {
        setIsFavourite(false);
      }
    };
    check();
  }, [business?._id, userLoggedIn]);

  const handleToggleFavourite = () => {
    if (!userLoggedIn) {
      navigate('/login', { state: { from: { pathname: window.location.pathname } } });
      return;
    }
    if (!business?._id) return;
    setFavouriteLoading(true);
    const id = business._id;
    const add = async () => {
      try {
        await api.post(`/favourites/${id}`);
        setIsFavourite(true);
        showSuccess('Added to your favourites!');
      } catch (err) {
        showError(err.response?.data?.message || 'Could not add to favourites');
      } finally {
        setFavouriteLoading(false);
      }
    };
    const remove = async () => {
      try {
        await api.delete(`/favourites/${id}`);
        setIsFavourite(false);
        showSuccess('Removed from your favourites');
      } catch (err) {
        showError(err.response?.data?.message || 'Could not remove from favourites');
      } finally {
        setFavouriteLoading(false);
      }
    };
    if (isFavourite) remove();
    else add();
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!userLoggedIn) {
      navigate('/login', { state: { from: { pathname: window.location.pathname } } });
      return;
    }

    if (!reviewData.rating || reviewData.rating === 0) {
      showError('Please select a rating');
      return;
    }

    setReviewLoading(true);
    try {
      if (editingReview) {
        await api.put(`/reviews/${editingReview._id}`, reviewData);
        showSuccess('Review updated successfully!');
      } else {
        await api.post(`/reviews/business/${business?.businessId}`, reviewData);
        showSuccess('Review submitted and published successfully!');
      }

      setReviewData({ rating: 0, comment: '' });
      setHoveredRating(0);
      setShowReviewForm(false);
      setEditingReview(null);

      const businessRes = await api.get(`/businesses/${categorySlug}/${businessSlug}`);
      setBusiness(businessRes.data);

      if (businessRes.data?.businessId) {
        const reviewsRes = await api.get(`/reviews/business/${businessRes.data.businessId}`);
        setReviews(reviewsRes.data);

        if (userLoggedIn && userInfo) {
          const existingReview = reviewsRes.data.find(
            review => {
              const reviewUserId = review.user?._id || review.user;
              return reviewUserId === userInfo.id || reviewUserId?.toString() === userInfo.id?.toString();
            }
          );
          setUserReview(existingReview || null);
        }
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Error submitting review');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setReviewData({
      rating: review.rating,
      comment: review.comment
    });
    setHoveredRating(0);
    setShowReviewForm(true);
  };

  const handleDeleteReview = async (reviewId) => {
    showConfirm(
      'Are you sure you want to delete this review? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/reviews/${reviewId}`);
          showSuccess('Review deleted successfully!');

          const businessRes = await api.get(`/businesses/${categorySlug}/${businessSlug}`);
          setBusiness(businessRes.data);

          if (businessRes.data?.businessId) {
            const reviewsRes = await api.get(`/reviews/business/${businessRes.data.businessId}`);
            setReviews(reviewsRes.data);
            setUserReview(null);
          }
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting review');
        }
      }
    );
  };

  const handleReportClick = () => {
    if (!userLoggedIn) {
      navigate('/login', { state: { from: { pathname: window.location.pathname } } });
      return;
    }
    setReportData({ reason: '', comment: '' });
    setShowReportModal(true);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!userLoggedIn || !business?.businessId) return;
    if (!reportData.reason || !reportData.comment?.trim()) {
      showError('Please select a reason and provide details');
      return;
    }
    setReportLoading(true);
    try {
      await api.post(`/reports/business/${business.businessId}`, {
        reason: reportData.reason,
        comment: reportData.comment.trim()
      });
      showSuccess('Report submitted. We will look into it.');
      setShowReportModal(false);
      setReportData({ reason: '', comment: '' });
    } catch (error) {
      showError(error.response?.data?.message || 'Error submitting report');
    } finally {
      setReportLoading(false);
    }
  };

  const isUserReview = (review) => {
    if (!userLoggedIn || !userInfo) return false;
    const reviewUserId = review.user?._id || review.user;
    return reviewUserId === userInfo.id || reviewUserId?.toString() === userInfo.id?.toString();
  };

  const calculateAverageRating = () => {
    if (business?.averageRating) {
      return business.averageRating;
    }
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const getReviewCount = () => {
    return business?.reviewCount || reviews.length;
  };

  const getDomainFromUrl = (url) => {
    if (!url) return '';
    try {
      let urlWithProtocol = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        urlWithProtocol = 'https://' + url;
      }
      const urlObj = new URL(urlWithProtocol);
      return urlObj.hostname.replace('www.', '');
    } catch (error) {
      return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    }
  };

  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';

  if (loading) {
    return <PageLoader message="Loading business..." fullScreen />;
  }

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-center p-8">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <LucideIcons.SearchX className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Business Not Found</h2>
          <p className="text-gray-500 mb-6">The business you are looking for does not exist or has been removed.</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const canonical = business.category?.slug && business.slug ? `/${business.category.slug}/${business.slug}` : undefined;
  const avg = parseFloat(calculateAverageRating()) || 0;
  const reviewCount = getReviewCount() || 0;

  // Sort images: primary image first, then by order
  const allImages = business.images && business.images.length > 0
    ? [...business.images].sort((a, b) => {
        if (a.isPrimary) return -1;
        if (b.isPrimary) return 1;
        return (a.order || 0) - (b.order || 0);
      })
    : [];

  const activeImage = allImages.length > 0
    ? (allImages[currentImageIndex] || allImages[0])
    : (business.category?.image ? { url: business.category.image } : null);

  const primaryImage = activeImage;

  const businessImageUrl = getImageUrl(primaryImage?.url || business.image);
  const imgUrl = businessImageUrl || undefined;

  const theme = getTheme(business.category?.theme || 'blue');

  const hasValidPhone = isValidPhone(business.phone);

  // Helper for rendering Category Icon
  const CategoryIcon = ({ className = "w-4 h-4" }) => {
    const cat = business?.category;
    if (!cat) return <LucideIcons.Store className={className} />;
    if (cat.icon && LucideIcons[cat.icon]) {
      const IconComp = LucideIcons[cat.icon];
      return <IconComp className={className} strokeWidth={1.75} />;
    }
    const slug = (cat.slug || cat.name || '').toLowerCase();
    if (slug.includes('educat') || slug.includes('school') || slug.includes('college')) return <LucideIcons.GraduationCap className={className} strokeWidth={1.75} />;
    if (slug.includes('health') || slug.includes('doctor') || slug.includes('hospital') || slug.includes('clinic')) return <LucideIcons.HeartPulse className={className} strokeWidth={1.75} />;
    if (slug.includes('hotel') || slug.includes('restaurant') || slug.includes('food') || slug.includes('cafe')) return <LucideIcons.UtensilsCrossed className={className} strokeWidth={1.75} />;
    if (slug.includes('retail') || slug.includes('shop') || slug.includes('store') || slug.includes('mall')) return <LucideIcons.ShoppingBag className={className} strokeWidth={1.75} />;
    if (slug.includes('construct') || slug.includes('build')) return <LucideIcons.HardHat className={className} strokeWidth={1.75} />;
    if (slug.includes('agri') || slug.includes('kisan') || slug.includes('farm')) return <LucideIcons.Leaf className={className} strokeWidth={1.75} />;
    if (slug.includes('real') || slug.includes('estate') || slug.includes('property')) return <LucideIcons.Building className={className} strokeWidth={1.75} />;
    if (slug.includes('service')) return <LucideIcons.Wrench className={className} strokeWidth={1.75} />;
    return <LucideIcons.Store className={className} strokeWidth={1.75} />;
  };

  const businessJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: business.name,
      description: ((typeof business.description === 'string' && business.description.replace(/<[^>]*>/g, '').slice(0, 200)) || `${business.name} is a verified ${business.category?.name || 'business'} in ${cityName || 'Fatehabad'}.`),
      url: `https://digitalfatehabad.com/b/${business.slug || business._id}`,
      telephone: business.phone ? `+91${business.phone.replace(/\D/g, '')}` : undefined,
      image: primaryImage?.url ? [getImageUrl(primaryImage.url)] : undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: [business.landmark, business.address].filter(Boolean).join(', ') || undefined,
        addressLocality: cityName || 'Fatehabad',
        addressRegion: 'Haryana',
        postalCode: business.pincode || '125050',
        addressCountry: 'IN'
      },
      ...(business.timings?.opening && business.timings?.closing ? {
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: business.timings.isOpenSunday 
              ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
              : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: business.timings.opening,
            closes: business.timings.closing
          }
        ]
      } : {}),
      ...(reviews && reviews.length > 0 ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: (reviews.reduce((acc, r) => acc + (r.rating || 5), 0) / reviews.length).toFixed(1),
          reviewCount: reviews.length
        }
      } : {}),
      priceRange: '₹₹'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://digitalfatehabad.com/'
        },
        ...(business.category ? [{
          '@type': 'ListItem',
          position: 2,
          name: business.category.name,
          item: `https://digitalfatehabad.com/${business.category.slug}`
        }] : []),
        {
          '@type': 'ListItem',
          position: business.category ? 3 : 2,
          name: business.name,
          item: `https://digitalfatehabad.com/b/${business.slug || business._id}`
        }
      ]
    }
  ];

  const pageTitle = `${business.name} in ${cityName || 'Fatehabad'} - Contact, Address, Reviews`;
  const pageDescription = `Find ${business.name} (${business.category?.name || 'Local Business'}) in ${cityName || 'Fatehabad'}. Phone: +91 ${business.phone || ''}, Address: ${business.address || ''}. View timings, photos & verified customer ratings.`;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        image={imgUrl}
        canonical={canonical}
        jsonLd={businessJsonLd}
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-20">
        {/* IMMERSIVE HERO HEADER */}
        <div className="relative w-full bg-gray-900 group">
          {/* Background Image / Gradient */}
          <div className="absolute inset-0 overflow-hidden">
            {primaryImage ? (
              <>
                <img
                  src={getImageUrl(primaryImage.url)}
                  alt={`${business.name} - ${business.category?.name || 'Shop'} in ${cityName || 'Fatehabad'}`}
                  className="w-full h-full object-cover blur-sm opacity-50 scale-105 group-hover:scale-100 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />
              </>
            ) : (
              <div
                className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800"
                style={{ background: `linear-gradient(135deg, #111827 0%, ${theme.colors.primary}40 100%)` }}
              />
            )}
          </div>

          <div className="container-custom relative pt-24 pb-12 md:h-[500px] lg:h-[550px] flex flex-col justify-end z-10">
            {/* Breadcrumb - Hidden on mobile for cleaner look */}
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-300 mb-6 backdrop-blur-md bg-black/20 w-fit px-4 py-1.5 rounded-full border border-white/10">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              {business.category && (
                <>
                  <Link to={`/${business.category.slug}`} className="hover:text-white transition-colors">{business.category.name}</Link>
                  <ChevronRight className="w-3 h-3" />
                </>
              )}
              {business.village && (
                <>
                  <Link to={`/village/${business.village.slug || business.village}`} className="hover:text-emerald-300 text-emerald-400 font-medium transition-colors flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {business.village.name}
                  </Link>
                  <ChevronRight className="w-3 h-3" />
                </>
              )}
              <span className="text-white font-medium truncate max-w-[200px]">{business.name}</span>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-end gap-6 md:gap-8 text-center md:text-left">
              {/* Mobile: Big Primary Image + Scrollable Thumbnails Below */}
              <div className="md:hidden w-full max-w-lg mx-auto mb-2">
                <div className="relative w-full aspect-[16/10] sm:aspect-[2/1] rounded-2xl overflow-hidden bg-gray-900 shadow-2xl border border-white/20">
                  {activeImage ? (
                    <img
                      src={getImageUrl(activeImage.url)}
                      alt={activeImage.caption || business.name}
                      className="w-full h-full object-cover transition-all duration-300"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center"
                      style={{
                        backgroundColor: `${theme.colors.primary}20`,
                        color: theme.colors.primary
                      }}
                    >
                      <CategoryIcon className="w-16 h-16" />
                      <span className="text-xs font-semibold text-white/90 mt-2">{business.category?.name}</span>
                    </div>
                  )}

                  {/* Photo Counter Badge */}
                  {allImages.length > 1 && (
                    <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 shadow-md">
                      <ImageIcon className="w-3.5 h-3.5" />
                      <span>{currentImageIndex + 1} / {allImages.length}</span>
                    </div>
                  )}

                  {/* Previous / Next Arrows on Big Image */}
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
                        }}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md text-white flex items-center justify-center transition-all active:scale-95 shadow-md"
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md text-white flex items-center justify-center transition-all active:scale-95 shadow-md"
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>

                {/* Scrollable Thumbnails Row Below */}
                {allImages.length > 1 && (
                  <div className="flex items-center gap-2.5 overflow-x-auto py-3 px-1 scrollbar-none snap-x">
                    {allImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`relative w-16 h-16 rounded-xl overflow-hidden shrink-0 border-2 transition-all snap-start ${
                          currentImageIndex === idx
                            ? 'border-primary-500 ring-2 ring-primary-500/50 scale-105 shadow-md'
                            : 'border-white/20 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={getImageUrl(img.url)}
                          alt={img.caption || `Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop Profile Image Box */}
              <div className="hidden md:block w-40 h-40 rounded-2xl bg-white dark:bg-gray-800 p-1.5 shadow-2xl shrink-0">
                <div className="w-full h-full rounded-xl overflow-hidden bg-gray-100 relative">
                  {activeImage ? (
                    <img src={getImageUrl(activeImage.url)} alt={business.name} className="w-full h-full object-cover" />
                  ) : (
                    <div
                      className="w-full h-full flex flex-col items-center justify-center"
                      style={{
                        backgroundColor: `${theme.colors.primary}15`,
                        color: theme.colors.primary
                      }}
                    >
                      <CategoryIcon className="w-12 h-12" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 text-white pb-4 md:pb-0">
                <div className="flex flex-wrap justify-center md:justify-start items-center gap-2 md:gap-3 mb-3">
                  {business.category && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-white/10 backdrop-blur-md border border-white/20 text-white">
                      <CategoryIcon />
                      {business.category.name}
                    </span>
                  )}
                  {business.village && (
                    <Link
                      to={`/village/${business.village.slug || business.village}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs md:text-sm font-semibold bg-emerald-500/20 backdrop-blur-md border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/30 transition-colors"
                      title={`View all businesses in ${business.village.name}`}
                    >
                      <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                      {business.village.name} ({business.village.block} Block)
                    </Link>
                  )}
                  {business.featured && (
                    <span className="px-3 py-1 rounded-full text-xs md:text-sm font-bold bg-amber-500/90 text-white shadow-lg shadow-amber-500/20 backdrop-blur-sm">
                      Featured
                    </span>
                  )}
                  {(reviewCount > 0 || avg > 0) && (
                    <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 text-xs md:text-sm">
                      <StarFilled className="w-3 h-3 md:w-4 md:h-4 text-amber-400" />
                      <span className="font-bold">{avg}</span>
                      <span className="text-gray-400">({reviewCount})</span>
                    </div>
                  )}
                </div>

                <h1 className="text-2xl md:text-5xl font-bold mb-2 md:mb-4 tracking-tight shadow-black drop-shadow-lg">{business.name}</h1>

                <div className="flex flex-wrap justify-center md:justify-start gap-4 md:gap-6 text-sm md:text-base text-gray-200">
                  {business.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary-400 shrink-0" />
                      <span className="line-clamp-1 text-left">{business.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop Actions */}
              <div className="hidden md:flex gap-3">
                <button
                  onClick={handleToggleFavourite}
                  className={`h-12 w-12 flex items-center justify-center rounded-xl backdrop-blur-md border transition-all hover:scale-105 ${isFavourite ? 'bg-red-500/20 border-red-500/50 text-red-500' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}`}
                >
                  <Heart className={`w-6 h-6 ${isFavourite ? 'fill-red-500' : ''}`} />
                </button>
                <button
                  onClick={handleReportClick}
                  className="h-12 w-12 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:scale-105 transition-all"
                  title="Report Business"
                >
                  <Flag className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN LAYOUT */}
        <div className="container-custom -mt-8 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* LEFT COLUMN: Main Content */}
            <div className="lg:col-span-2 space-y-8">

              {/* Only show gallery preview if images exist */}
              {allImages && allImages.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <ImageIcon className="w-5 h-5 text-primary-600" />
                      Gallery
                    </h2>
                    <span className="text-sm text-gray-500">{allImages.length} photos</span>
                  </div>

                  {/* Desktop Grid Gallery */}
                  <div className="hidden md:grid grid-cols-4 grid-rows-2 h-[300px] gap-2 rounded-xl overflow-hidden">
                    {allImages.slice(0, 5).map((img, idx) => (
                      <div
                        key={idx}
                        className={`relative group overflow-hidden cursor-pointer ${idx === 0 ? 'col-span-2 row-span-2' : ''}`}
                        onClick={() => setCurrentImageIndex(idx)}
                      >
                        <img src={getImageUrl(img.url)} alt="Gallery" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      </div>
                    ))}
                    {allImages.length > 5 && (
                      <div className="relative bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => setCurrentImageIndex(0)}>
                        <span className="font-bold text-gray-600">+{allImages.length - 5}</span>
                      </div>
                    )}
                  </div>

                  {/* Mobile Horizontal Scroll Gallery */}
                  <div className="md:hidden flex gap-3 overflow-x-auto pb-2 scrollbar-none snap-x">
                    {allImages.map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          setCurrentImageIndex(idx);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`relative w-44 h-32 rounded-2xl overflow-hidden shrink-0 cursor-pointer snap-start border-2 transition-all ${
                          currentImageIndex === idx ? 'border-primary-500 ring-2 ring-primary-500/40' : 'border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        <img src={getImageUrl(img.url)} alt="Gallery item" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* About Section */}
              <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100 dark:border-gray-800">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-600" />
                  About {business.name}
                </h2>
                {business.description ? (
                  <div
                    className="prose prose-sm md:prose-base dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: business.description }}
                  />
                ) : (
                  <p className="text-gray-500 italic">No description available for this business.</p>
                )}

                {/* Tags */}
                {business.tags && business.tags.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 block">Related Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {business.tags.map(tag => (
                        <Link
                          key={tag._id}
                          to={`/tag/${tag.slug}`}
                          className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg text-sm transition-colors border border-gray-100 dark:border-gray-700 hover:border-primary-200"
                        >
                          #{tag.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Reviews Section */}
              <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 md:p-8 shadow-sm border border-gray-100 dark:border-gray-800">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 mb-1">
                      <MessageSquare className="w-5 h-5 text-primary-600" />
                      Reviews
                    </h2>
                    <p className="text-sm text-gray-500">What people are saying</p>
                  </div>
                  {!showReviewForm && !userReview && (
                    <button
                      onClick={() => {
                        if (!userLoggedIn) {
                          navigate('/login', { state: { from: { pathname: window.location.pathname } } });
                        } else {
                          setShowReviewForm(true);
                          setEditingReview(null);
                          setReviewData({ rating: 0, comment: '' });
                          setHoveredRating(0);
                        }
                      }}
                      className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-all shadow-lg shadow-primary-600/20"
                    >
                      Write a Review
                    </button>
                  )}
                </div>



                {/* Review List */}
                {reviews.length > 0 ? (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review._id} className="border-b border-gray-100 dark:border-gray-800 pb-6 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800 flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold">
                              {(review.user?.name || 'User').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white">{review.user?.name || 'User'}</div>
                              <div className="text-xs text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</div>
                            </div>
                          </div>
                          {isUserReview(review) && (
                            <div className="flex gap-2">
                              <button onClick={() => handleEditReview(review)} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Edit</button>
                              <button onClick={() => handleDeleteReview(review._id)} className="text-xs text-red-500 hover:text-red-700 font-medium">Delete</button>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <StarFilled key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400' : 'text-gray-200 dark:text-gray-700'}`} />
                          ))}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="font-medium text-gray-900 dark:text-white">No reviews yet</h3>
                    <p className="text-sm text-gray-500">Be the first to share your experience!</p>
                  </div>
                )}

                {/* Review Form Modal/Inline */}
                {showReviewForm && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 md:p-8 w-full max-w-lg shadow-2xl animate-fadeIn">
                      <h3 className="text-2xl font-bold mb-6">{editingReview ? 'Edit Review' : 'Write a Review'}</h3>
                      <form onSubmit={handleReviewSubmit} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rating</label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                className="focus:outline-none transition-transform hover:scale-110"
                                onMouseEnter={() => setHoveredRating(star)}
                                onMouseLeave={() => setHoveredRating(0)}
                                onClick={() => setReviewData({ ...reviewData, rating: star })}
                              >
                                <StarFilled
                                  className={`w-8 h-8 ${star <= (hoveredRating || reviewData.rating) ? 'text-amber-400' : 'text-gray-200 dark:text-gray-700'}`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Experience</label>
                          <textarea
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none transition-all resize-none h-32"
                            placeholder="Tell us about your experience..."
                            value={reviewData.comment}
                            onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                            required
                          />
                        </div>
                        <div className="flex gap-3 pt-2">
                          <button type="button" onClick={() => setShowReviewForm(false)} className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-gray-700 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">Cancel</button>
                          <button type="submit" disabled={reviewLoading} className="flex-1 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20">
                            {reviewLoading ? 'Submitting...' : 'Submit Review'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Sticky Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">

                {/* Contact Card */}
                <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-800 overflow-hidden relative">
                  {/* Decorative blob */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rounded-bl-full pointer-events-none" />

                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Contact Info</h3>

                  <div className="space-y-5">
                    {hasValidPhone && (
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-green-600 dark:text-green-400 flex-shrink-0">
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">Phone Number</div>
                          <a
                            href={getTelLink(business.phone)}
                            onClick={() => trackPhoneView({
                              targetType: 'business',
                              targetId: business._id,
                              targetName: business.name,
                              targetCategory: business.category?.name || 'Business',
                              viewedPhone: business.phone,
                              action: 'call_click'
                            })}
                            className="text-base font-semibold text-gray-900 dark:text-white hover:text-primary-600 transition-colors block"
                          >
                            {formatPhoneDisplay(business.phone)}
                          </a>
                        </div>
                      </div>
                    )}

                    {getWhatsAppLink(business.phone) && (
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center text-green-600 dark:text-green-400 flex-shrink-0">
                          <MessageCircle className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">WhatsApp</div>
                          <a
                            href={getWhatsAppLink(business.phone)}
                            onClick={() => trackPhoneView({
                              targetType: 'business',
                              targetId: business._id,
                              targetName: business.name,
                              targetCategory: business.category?.name || 'Business',
                              viewedPhone: business.phone,
                              action: 'whatsapp_click'
                            })}
                            target="_blank"
                            rel="noreferrer"
                            className="text-base font-semibold text-green-600 hover:text-green-700 transition-colors block"
                          >
                            Chat on WhatsApp
                          </a>
                        </div>
                      </div>
                    )}

                    {business.website && !business.website.includes('barportal.in') && !business.website.includes('idbf.in') && (
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
                          <Globe className="w-5 h-5" />
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-xs text-gray-500 mb-0.5">Website</div>
                          <a href={business.website} target="_blank" rel="nofollow noopener noreferrer" className="text-base font-semibold text-gray-900 dark:text-white hover:text-primary-600 transition-colors block truncate">{getDomainFromUrl(business.website)}</a>
                        </div>
                      </div>
                    )}

                    {business.address && (
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">Location</div>
                          <div className="text-base font-medium text-gray-900 dark:text-white leading-snug">{business.address}</div>
                          <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 cursor-pointer hover:underline">
                            <Navigation className="w-3 h-3" /> Get Directions
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 grid grid-cols-2 gap-3">
                    {hasValidPhone ? (
                      <a
                        href={getTelLink(business.phone)}
                        onClick={() => trackPhoneView({
                          targetType: 'business',
                          targetId: business._id,
                          targetName: business.name,
                          targetCategory: business.category?.name || 'Business',
                          viewedPhone: business.phone,
                          action: 'call_click'
                        })}
                        className={`flex items-center justify-center gap-2 py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg shadow-green-600/20 transition-all active:scale-95 ${getWhatsAppLink(business.phone) ? 'col-span-1' : 'col-span-2'}`}
                      >
                        <Phone className="w-5 h-5" /> Call Now
                      </a>
                    ) : (
                      <button disabled className="col-span-2 py-3.5 bg-gray-100 text-gray-400 rounded-xl font-bold cursor-not-allowed">No Phone</button>
                    )}

                    {getWhatsAppLink(business.phone) && (
                      <a
                        href={getWhatsAppLink(business.phone)}
                        onClick={() => trackPhoneView({
                          targetType: 'business',
                          targetId: business._id,
                          targetName: business.name,
                          targetCategory: business.category?.name || 'Business',
                          viewedPhone: business.phone,
                          action: 'whatsapp_click'
                        })}
                        target="_blank"
                        rel="noreferrer"
                        className="col-span-1 flex items-center justify-center gap-2 py-3.5 bg-[#25D366] hover:bg-[#20ba59] text-white rounded-xl font-bold shadow-lg shadow-green-600/20 transition-all active:scale-95"
                      >
                        <MessageCircle className="w-5 h-5" /> WhatsApp
                      </a>
                    )}

                    {business.website && !business.website.includes('barportal.in') && !business.website.includes('idbf.in') && (
                      <a href={business.website} target="_blank" rel="nofollow noopener noreferrer" className="col-span-2 flex items-center justify-center gap-2 py-3.5 bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 hover:border-primary-600 dark:hover:border-primary-500 text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all">
                        <Globe className="w-5 h-5" /> Visit Website
                      </a>
                    )}
                  </div>
                </div>

                {/* Opening Hours Widget */}
                {business.hours && Object.keys(business.hours).length > 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary-600" />
                      Business Hours
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(business.hours).map(([day, hours]) => (
                        <div key={day} className="flex justify-between items-center text-sm py-1 border-b border-gray-50 last:border-0 dark:border-gray-800">
                          <span className="capitalize text-gray-500 w-24">{day}</span>
                          <span className={`font-medium ${hours.toLowerCase().includes('closed') ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>{hours}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobile Only: Floating Action Bar */}
                <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-4 z-50 flex gap-3 shadow-2xl">
                  {hasValidPhone && (
                    <a href={getTelLink(business.phone)} className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-xl font-bold shadow-lg shadow-green-600/20 active:scale-95 transition-transform">
                      <Phone className="w-5 h-5" /> Call
                    </a>
                  )}
                  {getWhatsAppLink(business.phone) && (
                    <a href={getWhatsAppLink(business.phone)} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white rounded-xl font-bold shadow-lg shadow-green-600/20 active:scale-95 transition-transform">
                      <MessageCircle className="w-5 h-5" /> Chat
                    </a>
                  )}
                  <button onClick={handleToggleFavourite} className={`p-3 rounded-xl border ${isFavourite ? 'bg-red-50 border-red-200 text-red-500' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                    <Heart className={`w-6 h-6 ${isFavourite ? 'fill-red-500' : ''}`} />
                  </button>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Image Gallery Modal (basic implementation) */}
        {/* Could be enhanced with a proper lightbox library later */}

        {/* Report Modal */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 w-full max-w-md shadow-2xl">
              <h3 className="text-xl font-bold mb-4">Report Business</h3>
              <form onSubmit={handleReportSubmit} className="space-y-4">
                <select
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                  value={reportData.reason}
                  onChange={(e) => setReportData({ ...reportData, reason: e.target.value })}
                >
                  <option value="">Select Reason</option>
                  {REPORT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <textarea
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 h-24 resize-none"
                  placeholder="Provide additional details..."
                  value={reportData.comment}
                  onChange={(e) => setReportData({ ...reportData, comment: e.target.value })}
                />
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowReportModal(false)} className="flex-1 py-3 rounded-xl border font-medium">Cancel</button>
                  <button type="submit" disabled={reportLoading} className="flex-1 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700">Submit</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BusinessDetail;
